/**
 * No shift open, shown before a sale ever starts — not discovered for the first time when a
 * cash tender fails. Cash still can't be taken without one (see `RecordPayment.cs`'s
 * `SHIFT_NOT_OPEN` guard and `ShiftRequiredPrompt`, which handles the moment it actually blocks
 * a payment); this banner is the quiet version of the same fact, visible the whole time it's
 * true, not just at the point of failure.
 *
 * Never blocks: search and adding to the cart both work with no shift open, exactly like
 * `ShiftPage`'s own copy says ("You can sell without one"). Tapping it is a shortcut to `/shift`,
 * not a requirement to dismiss anything before selling.
 */

import { useNavigate } from 'react-router';

import { Permission, can, selectActiveShopId, selectUser, useGetCurrentShiftQuery } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';

export function ShiftBanner() {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const shopId = useAppSelector(selectActiveShopId);

  const { data: shift, isLoading } = useGetCurrentShiftQuery(shopId ?? undefined, {
    skip: shopId === null || !can(user, Permission.ShiftViewOwn),
  });

  if (isLoading || shift !== null) return null;

  return (
    <button
      type="button"
      data-tour="shift-banner"
      onClick={() => void navigate('/shift')}
      className="flex min-h-compact w-full items-center gap-2 bg-warning-subtle px-4 py-2 text-left text-sm font-semibold text-warning-subtle-text"
      data-print="hide"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
        <rect x="2.5" y="6" width="19" height="12" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.7" />
      </svg>
      No shift open — cash sales will need one. Tap to open it now.
    </button>
  );
}
